import React from 'react';
import {
  AbsenceFilesPlaceholderIcon,
  Box,
  costFormater,
  Heading,
  Inscription,
  List,
  PaginationBar,
  Preloader,
  SearchInput,
} from '@common-fe/common-fe';

import { UnappliedFundsOrganization } from '../UnappliedFundsOrganizations.types';

import UnappliedFundsOrganizationItem from './UnappliedFundsOrganizationItem';

interface Props {
  organizations: UnappliedFundsOrganization[];
  total: number;
  totalUnappliedFunds: number;
  isTotalLoading?: boolean;
  isLoading: boolean;
  isSearchActive: boolean;
  searchString: string;
  page: number;
  perPage: number;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onManage: (orgId: number) => void;
}

const UnappliedFundsOrganizations: React.FC<Props> = ({
  organizations,
  total,
  totalUnappliedFunds,
  isTotalLoading,
  isLoading,
  isSearchActive,
  searchString,
  page,
  perPage,
  onSearchChange,
  onPageChange,
  onPerPageChange,
  onManage,
}) => {
  const isEmpty = !isLoading && organizations.length === 0;

  return (
    <Box data-testid="unapplied-funds-organizations" direction="column">
      <Box margin={{ bottom: 'spacing24' }}>
        <Heading
          level={1}
          size="regular"
          color="textTitle"
          data-testid="unapplied-funds-organizations_title"
        >
          Unapplied Funds
        </Heading>
      </Box>

      <Box margin={{ bottom: 'spacing24' }} direction="row" gap="spacing2">
        <Inscription color="textBody">Unapplied funds balance:</Inscription>
        <Inscription weight="bold" color="textBody" data-testid="unapplied-funds-organizations_total-balance">
          {isTotalLoading
            ? <Preloader testId="unapplied-funds-organizations_total-balance-preloader" size={16} />
            : costFormater(totalUnappliedFunds, true)}
        </Inscription>
      </Box>

      <Box pad="spacing24" background="module" round="container1Round">
        <Box direction="row" align="center" justify="between" margin={{ bottom: 'spacing24' }}>
          <Inscription weight="bold" color="textBody">
            Organization:{' '}
            <Inscription weight="bold" data-testid="unapplied-funds-organizations_count">
              {total}
            </Inscription>
          </Inscription>
          <Box width="280px">
            <SearchInput
              testId="unapplied-funds-organizations_search"
              value={searchString}
              placeholder="Search organization or amount"
              onChange={onSearchChange}
            />
          </Box>
        </Box>

        <List>
          {!isEmpty && (
            <List.Header>
              <List.Header.Item flex={4}>Organization</List.Header.Item>
              <List.Header.Item flex={2}>Unapplied funds</List.Header.Item>
              <List.Header.Item flex={2}>Unpaid invoices</List.Header.Item>
              <Box width="24px" margin={{ left: 'spacing24' }} />
            </List.Header>
          )}

          {isLoading && <List.Preloader testId="unapplied-funds-organizations_preloader" count={3} />}

          {isEmpty &&
            (isSearchActive ? (
              <List.Placeholder testId="unapplied-funds-organizations_no-results" />
            ) : (
              <List.Placeholder
                testId="unapplied-funds-organizations_empty"
                title="There are no unapplied funds"
                text={null}
                icon={<AbsenceFilesPlaceholderIcon size="xxlarge" color="iconSecondary" />}
              />
            ))}

          {!isLoading &&
            organizations.map((organization) => (
              <UnappliedFundsOrganizationItem
                key={organization.orgId}
                organization={organization}
                onManage={onManage}
              />
            ))}
        </List>

        {!isEmpty && (
          <PaginationBar
            page={page}
            total={total}
            pageSize={perPage}
            onChangePage={onPageChange}
            onChangePageSize={onPerPageChange}
          />
        )}
      </Box>
    </Box>
  );
};

export default UnappliedFundsOrganizations;
